/**
 * Filtro multi-seleção de status — padrão ec-actions-menu.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { IconFilter } from '@tabler/icons-react';
import { EcClickUpFilterChip } from '../../experiencia-cliente/components/EcClickUpFilterChip';
import { TECH_STATUS_TYPES } from '../utils/monitoramentoStatusUtils';
import '../../experiencia-cliente/components/ExperienciaClienteDetalhesModal.css';

function isAllSelected(selectedIds, options) {
  if (!options.length) return true;
  if (selectedIds == null) return true;
  return selectedIds.length >= options.length;
}

function isOptionSelected(id, selectedIds, options) {
  return isAllSelected(selectedIds, options) || (selectedIds || []).includes(id);
}

export function MonitoramentoStatusFilter({ options = [], selectedIds, onChange, disabled = false }) {
  const wrapRef = useRef(null);
  const btnRef = useRef(null);
  const [open, setOpen] = useState(false);

  const normalizedOptions = useMemo(
    () => (options || []).map((opt) => ({
      id: opt.id,
      label: opt.label || TECH_STATUS_TYPES[opt.id]?.label || opt.id
    })),
    [options]
  );

  const valueLabel = useMemo(() => {
    if (!normalizedOptions.length) return 'Todos';
    if (isAllSelected(selectedIds, normalizedOptions)) return 'Todos';
    const active = normalizedOptions.filter((opt) => isOptionSelected(opt.id, selectedIds, normalizedOptions));
    if (active.length <= 2) return active.map((o) => o.label).join(', ');
    return `${active.length} selecionados`;
  }, [normalizedOptions, selectedIds]);

  const hasPartialSelection = !isAllSelected(selectedIds, normalizedOptions) && normalizedOptions.length > 0;

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [open]);

  const handleToggleOption = useCallback((id) => {
    const allIds = normalizedOptions.map((o) => o.id);
    if (!allIds.length) return;

    let current = isAllSelected(selectedIds, normalizedOptions) ? [...allIds] : [...(selectedIds || [])];
    const idx = current.indexOf(id);
    if (idx >= 0) {
      current.splice(idx, 1);
      if (current.length === 0) current = [...allIds];
    } else {
      current.push(id);
    }

    if (current.length >= allIds.length) onChange(null);
    else onChange(current);
  }, [normalizedOptions, onChange, selectedIds]);

  const handleChipClick = useCallback(() => {
    if (disabled || !normalizedOptions.length) return;
    setOpen((v) => !v);
  }, [disabled, normalizedOptions.length]);

  return (
    <div className="ec-actions-menu-wrap monitoramento-status-filter-wrap" ref={wrapRef}>
      <EcClickUpFilterChip
        anchorRef={btnRef}
        label="Status"
        value={valueLabel}
        icon={IconFilter}
        hasSelection={hasPartialSelection}
        isOpen={open}
        disabled={disabled || !normalizedOptions.length}
        ariaLabel="Filtrar por status"
        onClick={handleChipClick}
      />
      {open && normalizedOptions.length > 0 ? (
        <div className="ec-actions-menu monitoramento-status-filter-menu" role="menu" aria-label="Filtrar por status">
          {normalizedOptions.map((opt) => {
            const selected = isOptionSelected(opt.id, selectedIds, normalizedOptions);
            return (
              <button
                key={opt.id}
                type="button"
                className={['ec-actions-menu-item', 'monitoramento-status-filter-item', selected ? 'is-selected' : ''].filter(Boolean).join(' ')}
                role="menuitemcheckbox"
                aria-checked={selected}
                onClick={() => handleToggleOption(opt.id)}
              >
                <span className="monitoramento-status-filter-check" aria-hidden="true">
                  {selected ? <Check size={14} strokeWidth={2.25} /> : null}
                </span>
                <span>{opt.label}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
